/* eslint-disable camelcase */
import { consoleLog } from '@Utils/consoleLog';
import { useClassStore } from '@Utils/useClassStore';
import Constants from 'expo-constants';
import { inject, injectable } from 'inversify';
import { action, computed, observable } from 'mobx';
import { geocodeByPlaceId } from 'react-google-places-autocomplete';
import { Platform } from 'react-native';

import container from '../../ioc/ioc';

import AccountManagementStore from '~stores/account-management';
import AuthStore from '~stores/authentication';
import type { Address, UserPrivacyConsentInfo } from '~types/gateways';
import {
  PendingAuthenticatedActions,
  PendingAuthenticationActionBlurb,
  ScreenSize,
} from '~types/views-types';

@injectable()
class AccountManagementPresenter {
  @inject(AuthStore)
  private AuthStore!: AuthStore;

  @inject(AccountManagementStore)
  private AccountManagementStore!: AccountManagementStore;

  @observable userEmail: string = '';
  @observable userName: string = '';
  @observable uiConfirmDisable: boolean = false;
  @observable uiDefaultAddressSelection: number | null = null;
  @observable uiDefaultCardSelection: number | null = null;
  @observable pinError: string = '';
  @observable uiAccountModalWidth: string = '929px';
  @observable userHasAttemptedSubmit: boolean = false;

  @action public setUiEditAddress = (newState: Address | null = null) => {
    this.AccountManagementStore.uiEditAddress = newState;
  };

  @action public setUiAccountModalWidth = (size: keyof typeof ScreenSize) => {
    if (size === ScreenSize.tabletLandscape) {
      this.uiAccountModalWidth = '50%';
    }
    if (size === ScreenSize.tabletPortrait) {
      this.uiAccountModalWidth = '90%';
    }
    if (size === ScreenSize.mobile) {
      this.uiAccountModalWidth = '95%';
    }
    if (size === ScreenSize.thinmobile) {
      this.uiAccountModalWidth = '95%';
    }
  };

  @action public setEditAddressId = (val: number | null) => {
    this.AccountManagementStore.editAddressId = val;
  };

  @action public setAddressDisplayName = (val: string) => {
    this.AccountManagementStore.addressDisplayName = val;
  };

  @action public setRecipientName = (val: string) =>
    (this.AccountManagementStore.addressRecipientName = val);

  @action public setAddressLineOne = (val: string) =>
    (this.AccountManagementStore.addressLineOne = val);

  @action setAddressPostCode = (val: string) =>
    (this.AccountManagementStore.addressPostCode = val);

  @action public setAddressCounty = (val: string) =>
    (this.AccountManagementStore.addressCounty = val);

  @action public setAddressTown = (val: string) =>
    (this.AccountManagementStore.addressTown = val);

  @action public setPhoneNumber = (val: string) =>
    (this.AccountManagementStore.addressPhoneNumber = val);

  @action private verifyFieldsComplete = () =>
    Boolean(
      this.AccountManagementStore.addressDisplayName &&
        this.AccountManagementStore.addressDisplayName.length > 0 &&
        this.AccountManagementStore.addressPostCode &&
        this.AccountManagementStore.addressLineOne &&
        this.AccountManagementStore.addressTown &&
        this.AccountManagementStore.addressPhoneNumber
    );

  @action public handleSetDefaultAddress = (id: number) => {
    this.AccountManagementStore.setDefaultAddress(id).then();
  };

  @action public handleSetDefaultCard = (id: number) => {
    this.AccountManagementStore.setDefaultCard(id).then();
  };

  @action setConfirmUi = (setting: boolean) => {
    this.uiConfirmDisable = setting;
  };

  @action public handleDeleteAddressClick = (id: number) => {
    if (this.AccountManagementStore.isExpired()) {
      this.AccountManagementStore.uiPasswordShown = true;
      return;
    }
    this.AccountManagementStore.deleteAddress(id).then();
  };

  @action public setUserHasAttemptedSubmit = (setting: boolean) =>
    (this.userHasAttemptedSubmit = setting);

  @computed get uiAddNewAddressPanelOpen() {
    return this.AccountManagementStore.uiAddNewAddressPanelOpen ?? false;
  }

  @computed get uiAddNewCardPanelOpen() {
    return this.AccountManagementStore.uiAddNewCreditCardPanelOpen ?? false;
  }

  @action public handleSaveClick = async () => {
    if (this.AccountManagementStore.isExpired()) {
      this.AccountManagementStore.uiPasswordShown = true;
      return;
    }
    this.AccountManagementStore.saveNewPassword().then();
  };

  @action public handleCancelClick = () => {
    this.setUiAddNewAddressPanelOpen(false);
    this.clearFields();
  };

  @action public handleEditAddressClick = async (
    propagateToAccountStore: boolean = true
  ) => {
    this.setUserHasAttemptedSubmit(true);
    if (!this.verifyFieldsComplete()) {
      if (__DEV__) {
        consoleLog(
          'Not saving edited address as some required fields are not complete'
        );
      }
      return false;
    }
    if (__DEV__) {
      consoleLog('Address validated, running onEditAddressPress callback');
    }
    if (propagateToAccountStore) {
      await this.AccountManagementStore.saveEditedUserAddress();
    }
    this.clearFields();
    this.setUiAddNewAddressPanelOpen(false);
    return true;
  };

  @action public handleAddAddressClick = async (
    propagateToAccountStore: boolean = true
  ) => {
    if (propagateToAccountStore && this.AccountManagementStore.isExpired()) {
      this.AccountManagementStore.uiPasswordShown = true;
      return false;
    }

    this.setUserHasAttemptedSubmit(true);
    if (!this.verifyFieldsComplete()) {
      if (__DEV__) {
        consoleLog(
          'Not adding address as some required fields are not complete'
        );
      }
      return false;
    }
    if (__DEV__) {
      consoleLog('Address validated, running onAddAddressPress callback');
    }

    if (propagateToAccountStore) {
      await this.AccountManagementStore.addUserAddress();
    }
    this.clearFields();
    this.setUiAddNewAddressPanelOpen(false);
    return true;
  };

  @action private clearFields = () => {
    this.setAddressDisplayName('');
    this.setRecipientName('');
    this.setAddressLineOne('');
    this.setAddressTown('');
    this.setPhoneNumber('');
    this.setAddressPostCode('');
    this.setAddressCounty('');
    this.setUserHasAttemptedSubmit(false);
  };

  @action public handleSubmitPasswordCheck = async () => {
    await this.AccountManagementStore.getToken(
      this.AccountManagementStore.passwordCheck
    );
  };

  @action handlePrivacyConsentToggle = async ({
    enable,
    onBeforeRequest = () => {
      /* do nothing */
    },
    onRequestComplete = () => {
      /* do nothing */
    },
    onRequestError = () => {
      /* do nothing */
    },
  }: Omit<UserPrivacyConsentInfo, 'token'>) => {
    if (this.AccountManagementStore.isExpired()) {
      this.AccountManagementStore.uiPasswordShown = true;
      return;
    }
    await this.AccountManagementStore.setUserPrivacyConsent({
      enable,
      onBeforeRequest,
      onRequestComplete,
      onRequestError,
    });
  };

  @action public setPasswordCheck = (passwordCheck: string) =>
    (this.AccountManagementStore.passwordCheck = passwordCheck);

  @action public setPassword = (password: string) =>
    (this.AccountManagementStore.password = password);

  @action public setConfirmPassword = (password: string) =>
    (this.AccountManagementStore.confirmPassword = password);

  @action public getUserSettings = async () => {
    await this.AccountManagementStore.getUserSettings();
  };

  @action public getAddressFromGooglePlaceId = async (placeId: string) => {
    let googleApiResponse;
    if (Platform.OS === 'web') {
      googleApiResponse = await geocodeByPlaceId(placeId);
    } else {
      const url = [
        'https://maps.googleapis.com/maps/api/place/details/json?',
        'place_id=',
        placeId,
        '&key=',
        Constants.manifest!.extra!.googlePlacesApi,
      ];
      googleApiResponse = await (await fetch(url.join(''))).json();
    }
    const address =
      Platform.OS === 'web'
        ? googleApiResponse?.[0]?.address_components
        : googleApiResponse?.result?.address_components;

    if (!address) {
      return null;
    }

    const number =
      address.find((c: any) => c.types.includes('street_number'))?.long_name ??
      '';

    const road =
      address.find((c: any) => c.types.includes('route'))?.long_name ?? '';

    const town =
      address.find((c: any) => c.types.includes('postal_town'))?.long_name ??
      '';

    const county =
      address.find((c: any) => c.types.includes('administrative_area_level_2'))
        ?.long_name ?? '';

    const postal_code =
      address.find((c: any) => c.types.includes('postal_code'))?.long_name ??
      '';

    return {
      number: number ?? '',
      road: road ?? '',
      town: town ?? '',
      county: county ?? '',
      postal_code: postal_code ?? '',
    };
  };

  @action public getAddress = async (placeId: string) => {
    const place = await this.getAddressFromGooglePlaceId(placeId);
    this.AccountManagementStore.addressLineOne = [
      place?.number,
      place?.road,
    ].join(' ');
    this.AccountManagementStore.addressTown = place?.town ?? '';
    this.AccountManagementStore.addressCounty = place?.county ?? '';
    this.AccountManagementStore.addressPostCode = place?.postal_code ?? '';
  };

  @action public checkToken = () => {
    if (
      this.AccountManagementStore.token &&
      this.AccountManagementStore.isExpired()
    ) {
      this.AccountManagementStore.uiPasswordShown = true;
    }
  };

  @action public checkUser = async () => {
    const user = await this.AuthStore.authCheck();
    if (!user) {
      this.setUiAddNewAddressPanelOpen(false);
      this.AuthStore.pendingAuthenticatedAction =
        PendingAuthenticatedActions.accountmanagement;
      this.AuthStore.setPendingAuthActionLoginDialogBlurb(
        PendingAuthenticationActionBlurb.accountmanagement
      );
      this.AuthStore.toggleLogin({ setting: true, method: 'login' });
      return;
    }
    if (
      this.AccountManagementStore.token &&
      !this.AccountManagementStore.isExpired()
    ) {
      return;
    }
    if (
      this.AccountManagementStore.token &&
      this.AccountManagementStore.isExpired()
    ) {
      this.AccountManagementStore.uiPasswordShown = true;
      return;
    }
    if (!this.AccountManagementStore.token) {
      this.AccountManagementStore.uiPasswordShown = true;
    }
  };

  @action setCloseModal = () => {
    this.AccountManagementStore.uiAccountManagementPanelOpen = false;
    this.AccountManagementStore.passwordCheck = '';
    this.AccountManagementStore.passwordCheckError = false;
  };

  @action setUiAddNewAddressPanelOpen = (
    newState: boolean = !this.AccountManagementStore.uiAddNewAddressPanelOpen
  ) => {
    this.AccountManagementStore.uiAddNewAddressPanelOpen = newState;
  };

  @action setUiAddNewCreditCardPanelOpen = (
    newState: boolean = !this.AccountManagementStore.uiAddNewCreditCardPanelOpen
  ) => {
    this.AccountManagementStore.uiAddNewCreditCardPanelOpen = newState;
  };

  @action handleRemoveCard = async (id: number) => {
    await this.AccountManagementStore.removeCard(id);
  };

  @computed get userSettings() {
    return this.AccountManagementStore.userSettings;
  }

  @action setUserEmail = (val: string) => (this.userEmail = val);
  @action setUserName = (val: string) => (this.userName = val);

  @action handleSaveDetails = () => {
    if (this.userName) {
      this.AccountManagementStore.setUserDetails(this.userName).then();
    }
  };

  @computed get getFullName() {
    return this.AuthStore.currentUser?.name ?? '';
  }

  @computed get getEmail() {
    return this.AuthStore.currentUser?.email ?? '';
  }
}

const useAccountManagementPresenter = () =>
  useClassStore<AccountManagementPresenter>(
    container.get(AccountManagementPresenter)
  );

export default useAccountManagementPresenter;
